import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import useUrlState from "@/hooks/useUrlState";

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

interface Notice {
  id: number;
  badge: string;
  title: string;
  content: string;
  created_at: string;
  is_pinned: boolean;
  is_visible: boolean;
  author_name: string | null;
}

const BADGE_COLORS: Record<string, string> = {
  공지: "bg-violet-500/20 text-violet-400",
  업데이트: "bg-sky-500/20 text-sky-400",
  이벤트: "bg-amber-500/20 text-amber-400",
  점검: "bg-red-500/20 text-red-400",
};

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).replace(/\. /g, ".").replace(/\.$/, "");
}

// 탭 복귀(리로드) 시 로딩 없이 마지막 데이터를 즉시 보여주기 위한 캐시
const NOTICES_CACHE_KEY = "cache:admin_notices";

function readNoticesCache(): Notice[] | null {
  try {
    const raw = sessionStorage.getItem(NOTICES_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Notice[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeNoticesCache(list: Notice[]): void {
  try {
    sessionStorage.setItem(NOTICES_CACHE_KEY, JSON.stringify(list));
  } catch {
    // 캐시 저장 실패는 무시
  }
}

interface NoticeFormModalProps {
  notice?: Notice;
  onSave: (values: {
    id?: number;
    badge: string;
    title: string;
    content: string;
    is_pinned: boolean;
    is_visible: boolean;
  }) => Promise<void>;
  onClose: () => void;
}

function NoticeFormModal({ notice, onSave, onClose }: NoticeFormModalProps) {
  const [badge, setBadge] = useState<string>(notice?.badge ?? "공지");
  const [title, setTitle] = useState(notice?.title ?? "");
  const [content, setContent] = useState(notice?.content ?? "");
  const [isPinned, setIsPinned] = useState(notice?.is_pinned ?? false);
  const [isVisible, setIsVisible] = useState(notice?.is_visible ?? true);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim()) return;
    setSaving(true);
    try {
      await onSave({
        id: notice?.id,
        badge,
        title: title.trim(),
        content: content.trim(),
        is_pinned: isPinned,
        is_visible: isVisible,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#13131f] border border-white/[0.1] rounded-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-white/[0.08]">
          <h3 className="text-white font-semibold text-base">{notice ? "공지사항 수정" : "새 공지사항 작성"}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer transition-colors">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex gap-2 flex-wrap">
            {(["공지", "업데이트", "이벤트", "점검"] as const).map((b) => (
              <button
                key={b}
                onClick={() => setBadge(b)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all border ${
                  badge === b
                    ? `${BADGE_COLORS[b]} border-current`
                    : "text-gray-500 border-white/[0.08] hover:text-gray-300"
                }`}
              >
                {b}
              </button>
            ))}
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1.5 block">제목</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="공지 제목을 입력하세요"
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-rose-500/50"
            />
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1.5 block">내용</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value.slice(0, 500))}
              placeholder="공지 내용을 입력하세요 (최대 500자)"
              rows={5}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-rose-500/50 resize-none"
            />
            <p className="text-gray-600 text-xs text-right mt-1">{content.length}/500</p>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <div
                onClick={() => setIsPinned(!isPinned)}
                className={`w-10 h-5 rounded-full transition-colors cursor-pointer ${isPinned ? "bg-rose-600" : "bg-white/[0.1]"}`}
              >
                <div className={`w-4 h-4 bg-white rounded-full mt-0.5 transition-transform ${isPinned ? "translate-x-5" : "translate-x-0.5"}`}></div>
              </div>
              <span className="text-gray-400 text-sm">상단 고정</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <div
                onClick={() => setIsVisible(!isVisible)}
                className={`w-10 h-5 rounded-full transition-colors cursor-pointer ${isVisible ? "bg-emerald-600" : "bg-white/[0.1]"}`}
              >
                <div className={`w-4 h-4 bg-white rounded-full mt-0.5 transition-transform ${isVisible ? "translate-x-5" : "translate-x-0.5"}`}></div>
              </div>
              <span className="text-gray-400 text-sm">즉시 게시</span>
            </label>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleSubmit}
              disabled={!title.trim() || !content.trim() || saving}
              className="flex-1 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 disabled:opacity-40 text-white text-sm font-medium cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2"
            >
              {saving && <i className="ri-loader-4-line animate-spin"></i>}
              {notice ? "수정 저장" : "등록"}
            </button>
            <button
              onClick={onClose}
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] text-gray-300 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
            >
              취소
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function NoticesTab() {
  const { session } = useAuth();
  const cachedNoticesRef = useRef<Notice[] | null>(readNoticesCache());
  const [notices, setNotices] = useState<Notice[]>(cachedNoticesRef.current ?? []);
  const [loading, setLoading] = useState(cachedNoticesRef.current === null);
  const [error, setError] = useState("");
  // 검색·필터·페이지 상태를 주소(URL)에도 남겨 새로고침·링크 공유에도 유지 (sessionStorage 백업)
  const [search, setSearch] = useUrlState("notice_q", "");
  const [badgeFilter, setBadgeFilter] = useUrlState("notice_badge", "전체");
  const [statusFilter, setStatusFilter] = useUrlState("notice_status", "전체");
  const [pageRaw, setPageRaw] = useUrlState("notice_page", "1");
  const page = Math.max(1, parseInt(pageRaw, 10) || 1);
  const setPage = (next: number | ((prev: number) => number)) => {
    const target = typeof next === "function" ? next(page) : next;
    setPageRaw(String(Math.max(1, target)));
  };
  const PER_PAGE = 10;
  const [modalOpen, setModalOpen] = useState(false);
  const [editNotice, setEditNotice] = useState<Notice | undefined>();
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);

  const accessToken = session?.access_token;

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchNotices = useCallback(async () => {
    if (!accessToken) return;
    // 캐시된 데이터가 이미 있으면 로딩 화면 없이 조용히 갱신
    if (cachedNoticesRef.current === null) setLoading(true);
    setError("");
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/get-admin-notices`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      const list = (json.notices ?? []) as Notice[];
      setNotices(list);
      writeNoticesCache(list);
    } catch (err: any) {
      console.error("[Notices] fetch error:", err);
      // 캐시된 데이터가 있으면 마지막 데이터를 유지하고 조용히 실패 처리
      if (cachedNoticesRef.current === null) {
        setError(err.message || "공지사항 목록을 불러오는 중 오류가 발생했습니다.");
      }
    } finally {
      cachedNoticesRef.current = cachedNoticesRef.current ?? [];
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const apiPost = async (slug: string, body: object) => {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/${slug}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: SUPABASE_ANON_KEY,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.error || `HTTP ${res.status}`);
    }
    return res.json();
  };

  const handleSave = async (values: {
    id?: number;
    badge: string;
    title: string;
    content: string;
    is_pinned: boolean;
    is_visible: boolean;
  }) => {
    if (!accessToken) return;
    try {
      if (values.id) {
        await apiPost("update-admin-notice", {
          id: values.id,
          title: values.title,
          content: values.content,
          badge: values.badge,
          is_pinned: values.is_pinned,
          is_visible: values.is_visible,
        });
        showToast("공지사항이 수정되었습니다.");
      } else {
        await apiPost("create-admin-notice", {
          title: values.title,
          content: values.content,
          badge: values.badge,
          is_pinned: values.is_pinned,
          is_visible: values.is_visible,
        });
        showToast("새 공지사항이 등록되었습니다.");
      }
      await fetchNotices();
      setModalOpen(false);
      setEditNotice(undefined);
    } catch (err: any) {
      console.error("[Notices] save error:", err);
      showToast(err.message || "저장 중 오류가 발생했습니다.", "error");
    }
  };

  const handleDelete = async (id: number) => {
    if (!accessToken) return;
    if (!window.confirm("이 공지사항을 삭제하시겠습니까?")) return;
    try {
      await apiPost("delete-admin-notice", { id });
      showToast("공지사항이 삭제되었습니다.");
      await fetchNotices();
    } catch (err: any) {
      console.error("[Notices] delete error:", err);
      showToast(err.message || "삭제 중 오류가 발생했습니다.", "error");
    }
  };

  const handleToggleField = async (id: number, field: "is_pinned" | "is_visible", value: boolean) => {
    if (!accessToken) return;
    try {
      await apiPost("update-admin-notice", { id, [field]: value });
      showToast(field === "is_pinned" ? "고정 상태가 변경되었습니다." : "게시 상태가 변경되었습니다.");
      await fetchNotices();
    } catch (err: any) {
      console.error("[Notices] toggle error:", err);
      showToast(err.message || "상태 변경 중 오류가 발생했습니다.", "error");
    }
  };

  const publishedCount = notices.filter((n) => n.is_visible).length;

  const filtered = useMemo(() => {
    let result = [...notices];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (n) =>
          n.title.toLowerCase().includes(q) ||
          n.content.toLowerCase().includes(q) ||
          (n.author_name?.toLowerCase() ?? "").includes(q)
      );
    }
    if (badgeFilter !== "전체") {
      result = result.filter((n) => n.badge === badgeFilter);
    }
    if (statusFilter !== "전체") {
      result = result.filter((n) => (statusFilter === "게시중" ? n.is_visible : !n.is_visible));
    }
    return result;
  }, [notices, search, badgeFilter, statusFilter]);

  // 필터가 실제로 바뀌면 첫 페이지로 (최초 복원 시에는 페이지 유지)
  const skipPageResetRef = useRef(true);
  useEffect(() => {
    if (skipPageResetRef.current) {
      skipPageResetRef.current = false;
      return;
    }
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, badgeFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const paged = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-semibold text-lg">공지사항 관리</h2>
          <p className="text-gray-500 text-sm mt-0.5">
            총 {notices.length}건 (게시중 {publishedCount}건)
          </p>
        </div>
        <button
          onClick={() => { setEditNotice(undefined); setModalOpen(true); }}
          className="flex items-center gap-2 bg-rose-600 hover:bg-rose-500 text-white px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-add-line"></i>
          새 공지 작성
        </button>
      </div>

      {/* 검색 + 필터 */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm w-full">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="제목, 내용, 작성자 검색"
            className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-rose-500/40 transition-colors"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center bg-white/[0.04] border border-white/[0.08] rounded-lg p-0.5 gap-0.5">
            {["전체", "공지", "업데이트", "이벤트", "점검"].map((b) => (
              <button
                key={b}
                onClick={() => setBadgeFilter(b)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                  badgeFilter === b ? "bg-rose-600 text-white" : "text-gray-400 hover:text-white"
                }`}
              >
                {b}
              </button>
            ))}
          </div>
          <div className="flex items-center bg-white/[0.04] border border-white/[0.08] rounded-lg p-0.5 gap-0.5">
            {["전체", "게시중", "임시저장"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                  statusFilter === s ? "bg-rose-600 text-white" : "text-gray-400 hover:text-white"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && notices.length === 0 && (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5 animate-pulse">
              <div className="flex items-start gap-3">
                <div className="w-16 h-5 bg-white/[0.06] rounded-full shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="w-1/2 h-4 bg-white/[0.06] rounded"></div>
                  <div className="w-full h-3 bg-white/[0.06] rounded"></div>
                  <div className="w-1/3 h-3 bg-white/[0.06] rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
          <i className="ri-error-warning-line text-red-400"></i>
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-3">
        {filtered.length === 0 && !loading ? (
          <div className="text-center py-16 text-gray-600 text-sm">
            <i className="ri-file-list-3-line text-2xl mb-2 block"></i>
            조건에 맞는 공지사항이 없습니다.
          </div>
        ) : (
          paged.map((n) => (
            <div
              key={n.id}
              className={`bg-white/[0.03] border rounded-2xl p-5 transition-colors ${n.is_pinned ? "border-rose-500/30" : "border-white/[0.08]"}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="flex items-center gap-2 mt-0.5 shrink-0">
                    {n.is_pinned && (
                      <i className="ri-pushpin-2-fill text-rose-400 text-sm"></i>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${BADGE_COLORS[n.badge] || "bg-white/[0.06] text-gray-400"}`}>
                      {n.badge}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white text-sm font-semibold truncate">{n.title}</p>
                      {!n.is_visible && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400 font-medium whitespace-nowrap shrink-0">임시저장</span>
                      )}
                    </div>
                    <p className="text-gray-500 text-xs mt-1 line-clamp-2">{n.content}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <p className="text-gray-600 text-xs">{formatDate(n.created_at)}</p>
                      {n.author_name && (
                        <p className="text-gray-600 text-xs">· {n.author_name}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleToggleField(n.id, "is_pinned", !n.is_pinned)}
                    title={n.is_pinned ? "고정 해제" : "상단 고정"}
                    className={`w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${n.is_pinned ? "bg-rose-500/20 text-rose-400 hover:bg-rose-500/30" : "bg-white/[0.06] text-gray-500 hover:text-white hover:bg-white/[0.1]"}`}
                  >
                    <i className="ri-pushpin-2-line text-xs"></i>
                  </button>
                  <button
                    onClick={() => handleToggleField(n.id, "is_visible", !n.is_visible)}
                    title={n.is_visible ? "비공개 처리" : "게시"}
                    className={`w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${n.is_visible ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30" : "bg-white/[0.06] text-gray-500 hover:text-white hover:bg-white/[0.1]"}`}
                  >
                    <i className={`${n.is_visible ? "ri-eye-line" : "ri-eye-off-line"} text-xs`}></i>
                  </button>
                  <button
                    onClick={() => { setEditNotice(n); setModalOpen(true); }}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/[0.06] text-gray-500 hover:text-white hover:bg-white/[0.1] cursor-pointer transition-colors"
                  >
                    <i className="ri-pencil-line text-xs"></i>
                  </button>
                  <button
                    onClick={() => handleDelete(n.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/[0.06] text-gray-500 hover:text-red-400 hover:bg-red-500/10 cursor-pointer transition-colors"
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (() => {
        const PAGE_WINDOW = 10;
        const currentWindow = Math.ceil(safePage / PAGE_WINDOW);
        const windowStart = (currentWindow - 1) * PAGE_WINDOW + 1;
        const windowEnd = Math.min(currentWindow * PAGE_WINDOW, totalPages);
        const hasPrevWindow = windowStart > 1;
        const hasNextWindow = windowEnd < totalPages;
        const pagesInWindow = Array.from({ length: windowEnd - windowStart + 1 }, (_, i) => windowStart + i);

        return (
          <div className="flex items-center justify-center gap-1 pt-2">
            <button
              onClick={() => setPage(1)}
              disabled={safePage === 1}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
            >
              <i className="ri-skip-left-line text-sm"></i>
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-s-line text-sm"></i>
            </button>

            {hasPrevWindow && (
              <button
                onClick={() => setPage(windowStart - 1)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-xs text-gray-500 hover:text-white hover:bg-white/[0.05] cursor-pointer transition-colors"
              >
                ...
              </button>
            )}

            {pagesInWindow.map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  p === safePage
                    ? "bg-rose-500/20 border border-rose-500/30 text-rose-400"
                    : "text-gray-500 hover:text-white hover:bg-white/[0.05]"
                }`}
              >
                {p}
              </button>
            ))}

            {hasNextWindow && (
              <button
                onClick={() => setPage(windowEnd + 1)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-xs text-gray-500 hover:text-white hover:bg-white/[0.05] cursor-pointer transition-colors"
              >
                ...
              </button>
            )}

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-s-line text-sm"></i>
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={safePage === totalPages}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
            >
              <i className="ri-skip-right-line text-sm"></i>
            </button>
          </div>
        );
      })()}

      {modalOpen && (
        <NoticeFormModal
          notice={editNotice}
          onSave={handleSave}
          onClose={() => { setModalOpen(false); setEditNotice(undefined); }}
        />
      )}

      {toast && (
        <div className={`fixed bottom-6 right-6 z-[60] flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
          toast.type === "success"
            ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-300"
            : "bg-red-500/20 border-red-500/30 text-red-300"
        }`}>
          <i className={toast.type === "success" ? "ri-checkbox-circle-line" : "ri-close-circle-line"}></i>
          {toast.msg}
        </div>
      )}
    </div>
  );
}